/**
 * MarkdownMessage V2 — Formatted markdown with code headers + copy button.
 *
 * Tree blocks (lines with box-drawing chars ├ └ │ ─) are extracted BEFORE
 * markdown parsing and rendered as raw <pre> elements so newlines are never
 * collapsed by the markdown AST.
 */

import { useState, useCallback } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeHighlight from "rehype-highlight";
import { Copy, Check, Download } from "lucide-react";

interface MarkdownMessageProps {
  content: string;
}

// ── Tree detection ────────────────────────────────────────────────────────────

const TREE_RE = /[├└│─]/;

interface Segment {
  text: string;
  isTree: boolean;
}

/**
 * Split raw markdown content into alternating segments:
 *   • isTree=false → normal markdown, passed to ReactMarkdown
 *   • isTree=true  → file-tree block, rendered as <pre> to preserve newlines
 *
 * Lines that immediately precede a tree line (root directory names without
 * tree chars) are pulled into the tree segment via one-line look-ahead.
 * Content already inside fenced code blocks is left untouched.
 */
function splitIntoSegments(content: string): Segment[] {
  const lines = content.split("\n");
  const result: Segment[] = [];
  let buffer: string[] = [];
  let currentIsTree = false;
  let inFence = false;

  const flush = () => {
    if (buffer.length === 0) return;
    const text = buffer.join("\n");
    // Skip leading-empty-only segments before any real content
    if (!text.trim() && result.length === 0) return;
    result.push({ text, isTree: currentIsTree });
    buffer = [];
  };

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i] ?? "";

    // Track fenced code blocks — never reclassify content inside them
    if (/^`{3,}/.test(line)) {
      if (currentIsTree) { flush(); currentIsTree = false; }
      inFence = !inFence;
      buffer.push(line);
      continue;
    }

    if (!inFence) {
      const hasTree = TREE_RE.test(line);
      const nextHasTree = i + 1 < lines.length && TREE_RE.test(lines[i + 1] ?? "");
      // Root-dir lookahead: pull in the preceding line only if it looks like a
      // short path token (ends with / or has no spaces) — never pull prose.
      const looksLikePath = /\/$/.test(line.trim()) || !/\s/.test(line.trim());
      const isTreeLine = hasTree || (nextHasTree && line.trim() !== "" && looksLikePath);

      if (isTreeLine !== currentIsTree) {
        flush();
        currentIsTree = isTreeLine;
      }
      buffer.push(line);
    } else {
      // Inside a fenced block — always non-tree
      if (currentIsTree) { flush(); currentIsTree = false; }
      buffer.push(line);
    }
  }
  flush();
  return result;
}

// ── Code block component ──────────────────────────────────────────────────────

function CodeBlock({ className, children }: { className?: string; children: React.ReactNode }) {
  const [copied, setCopied] = useState(false);
  const text = String(children).replace(/\n$/, "");
  const lang = className?.replace("hljs language-", "").replace("language-", "") ?? "";

  const handleCopy = useCallback(() => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }, [text]);

  return (
    <div className="my-3 border border-[var(--bw-glass-border)] overflow-hidden"
      style={{ clipPath: "polygon(0 0, calc(100% - 8px) 0, 100% 8px, 100% 100%, 0 100%)" }}
    >
      <div className="flex items-center justify-between px-3 py-1.5 bg-[var(--bw-surface-2)]/80 border-b border-[var(--bw-glass-border)]">
        <span className="text-[9px] uppercase tracking-[0.15em] text-[var(--bw-muted)] font-mono">
          {lang || "code"}
        </span>
        <button
          onClick={handleCopy}
          className="flex items-center gap-1 text-[9px] text-[var(--bw-muted)] hover:text-[var(--bw-accent)] transition-colors"
        >
          {copied ? <Check size={10} className="text-[var(--bw-success)]" /> : <Copy size={10} />}
          {copied ? "copied" : "copy"}
        </button>
      </div>
      <pre className="p-3 overflow-x-auto text-[0.8em] leading-relaxed font-mono bg-[var(--bw-bg)]/80">
        <code className={className}>{children}</code>
      </pre>
    </div>
  );
}

// ── Markdown components map ───────────────────────────────────────────────────

const MD_COMPONENTS: React.ComponentProps<typeof ReactMarkdown>["components"] = {
  code({ className, children, ...props }) {
    const isBlock = !!className;
    if (isBlock) {
      return <CodeBlock className={className}>{children}</CodeBlock>;
    }
    return (
      <code
        className="bg-[var(--bw-surface-2)] text-[var(--bw-accent)] px-1.5 py-0.5 text-[0.85em] font-mono border border-[var(--bw-glass-border)]"
        {...props}
      >
        {children}
      </code>
    );
  },
  pre({ children }) { return <>{children}</>; },
  h1({ children }) { return <h1 className="text-lg font-bold text-[var(--bw-accent)] mt-4 mb-2 font-[var(--font-heading)] uppercase tracking-wider">{children}</h1>; },
  h2({ children }) { return <h2 className="text-base font-bold text-[var(--bw-accent)] mt-3 mb-1.5 font-[var(--font-heading)] uppercase tracking-wider">{children}</h2>; },
  h3({ children }) { return <h3 className="text-sm font-bold text-[var(--bw-fg)] mt-2 mb-1">{children}</h3>; },
  p({ children }) { return <p className="mb-2 last:mb-0 leading-relaxed">{children}</p>; },
  a({ href, children }) {
    if (href?.startsWith("/api/share/")) {
      return (
        <a
          href={href}
          download
          className="inline-flex items-center gap-1.5 px-3 py-1.5 my-1 border border-[var(--bw-accent)]/60 text-[var(--bw-accent)] text-[11px] font-mono uppercase tracking-wider hover:bg-[var(--bw-accent)]/10 transition-colors cursor-pointer"
          style={{ clipPath: "polygon(0 0, calc(100% - 6px) 0, 100% 6px, 100% 100%, 0 100%)" }}
        >
          <Download size={12} />
          {children}
        </a>
      );
    }
    return <a href={href} target="_blank" rel="noopener noreferrer" className="text-[var(--bw-accent)] underline underline-offset-2 hover:brightness-125">{children}</a>;
  },
  ul({ children }) { return <ul className="list-disc list-inside mb-2 space-y-0.5 pl-1">{children}</ul>; },
  ol({ children }) { return <ol className="list-decimal list-inside mb-2 space-y-0.5 pl-1">{children}</ol>; },
  li({ children }) { return <li className="leading-relaxed">{children}</li>; },
  blockquote({ children }) {
    return <blockquote className="border-l-2 border-[var(--bw-accent)]/50 pl-3 my-2 text-[var(--bw-muted)] italic">{children}</blockquote>;
  },
  table({ children }) {
    return (
      <div className="overflow-x-auto my-3 border border-[var(--bw-glass-border)]">
        <table className="w-full text-xs border-collapse">{children}</table>
      </div>
    );
  },
  thead({ children }) { return <thead className="bg-[var(--bw-surface-2)]/50">{children}</thead>; },
  th({ children }) { return <th className="text-left px-3 py-1.5 text-[var(--bw-accent)] uppercase text-[10px] tracking-wider border-b border-[var(--bw-glass-border)]">{children}</th>; },
  td({ children }) { return <td className="px-3 py-1.5 border-b border-[var(--bw-glass-border)]/50">{children}</td>; },
  hr() { return <hr className="border-[var(--bw-glass-border)] my-4" />; },
  strong({ children }) { return <strong className="font-bold text-[var(--bw-fg)]">{children}</strong>; },
  em({ children }) { return <em className="italic text-[var(--bw-accent)]/80">{children}</em>; },
};

// ── Main component ────────────────────────────────────────────────────────────

export function MarkdownMessage({ content }: MarkdownMessageProps) {
  const segments = splitIntoSegments(content);

  return (
    // min-w-0 + overflow-x-hidden anchors the flex-item so children can't
    // push the bubble wider than max-w-[85%]; the <pre> uses overflow-x-auto
    // to scroll within this constrained box.
    <div className="min-w-0 w-full overflow-x-hidden">
      {segments.map((seg, i) =>
        seg.isTree ? (
          // Raw <pre> — React text node preserves \n without markdown touching it
          <pre
            key={i}
            className="my-2 p-3 overflow-x-auto text-[0.8em] leading-relaxed font-mono bg-[var(--bw-bg)]/80 border border-[var(--bw-glass-border)]"
          >
            {seg.text}
          </pre>
        ) : (
          <ReactMarkdown
            key={i}
            remarkPlugins={[remarkGfm]}
            rehypePlugins={[rehypeHighlight]}
            components={MD_COMPONENTS}
          >
            {seg.text}
          </ReactMarkdown>
        )
      )}
    </div>
  );
}
